<template>
  <v-container class="aoi-editor-container">
    <Map
      v-if="hasWebGL && mapOptions"
      :options="mapOptions"
      :id="mapId"
      height="30vh"
      :show-base-layer-control="false"
      :show-search-control="true"
      :show-places="true"
      @map-on-load="mapLoaded"
      @map-on-style-data="mapStyleData"
    ></Map>
  </v-container>
</template>

<script>
import lodashGet from "lodash/get";
import lodashDebounce from "lodash/debounce";
import lodashIsEqual from "lodash/isEqual";
import lodashOrderBy from "lodash/orderBy";

import { getBboxFromFeatures } from "@/common/maps/utilities";
import { MODULE_PREFIX_MAP } from "@/store";
import { mapGetters } from "vuex";
import { mapPolygonData } from "@/common/maps/features";
import { getDefaultPolygonOptions } from "@/common/maps/styles";
import {
  ALERT_AOI_DISPLAY_MAP,
  WORLD_WIDE_AOI,
} from "@/common/constants/alert.js";
import { removeMapData } from "@/common/maps/features";
import {
  addToMap as addCountriesToMap,
  addToMap as addCitiesToMap,
} from "@/services/grids/stability.service";
import { getAllCountries, getAllCities } from "@/common/maps/utilities";

export default {
  name: "AoiDisplay",
  components: {
    Map: () => import("@/components/map/Map"),
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      hasWebGL: this.isAvailableWebGL(),
      aoiFeature: {},
      mapId: ALERT_AOI_DISPLAY_MAP,
      countriesAll: [],
      countriesLayerId: "layer-country",
      countriesLayerFieldId: "_id",
      citiesAll: [],
      citiesLayerId: "layer-city",
      citiesLayerFieldId: "_id",
    };
  },
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
    }),
    map() {
      return this.maps[this.mapId];
    },
    mapOptions() {
      return {
        hideNavigationControls: true,
        hideScaleControls: true,
        attributionCompact: true,
        zoom: 0,
        bounds: [-175, -85, 178, 85],
      };
    },
    isWorldWideAOI() {
      return (
        this.value &&
        this.value.properties &&
        lodashIsEqual(this.value.properties, WORLD_WIDE_AOI.properties)
      );
    },
    hasGeometry() {
      return !!lodashGet(this.value, "geometry");
    },
    hasCountriesOrCities() {
      const AOI_IDS = lodashGet(this.value, "properties.aoiIds");
      return !!(AOI_IDS && Array.isArray(AOI_IDS) && AOI_IDS.length);
    },
    countries() {
      const AOI_IDS = lodashGet(this.value, "properties.aoiIds");
      let countries = [];
      if (
        AOI_IDS &&
        Array.isArray(AOI_IDS) &&
        AOI_IDS.length &&
        this.countriesAll &&
        Array.isArray(this.countriesAll) &&
        this.countriesAll.length
      ) {
        const LEN = this.countriesAll.length;
        for (let i = 0; i < LEN; i++) {
          const COUNTRY = this.countriesAll[i];
          if (lodashGet(COUNTRY, "properties.iso2")) {
            AOI_IDS.forEach((id) => {
              if (COUNTRY.properties.iso2.toUpperCase() === id.toUpperCase()) {
                countries.push(COUNTRY);
              }
            });
          }
        }
      }
      if (countries.length) {
        countries = lodashOrderBy(
          countries,
          ["properties.displayName"],
          ["asc"]
        );
        return countries;
      }
      return null;
    },
    cities() {
      const AOI_IDS = lodashGet(this.value, "properties.aoiIds");
      let cities = [];
      if (
        AOI_IDS &&
        Array.isArray(AOI_IDS) &&
        AOI_IDS.length &&
        this.citiesAll &&
        Array.isArray(this.citiesAll) &&
        this.citiesAll.length
      ) {
        const LEN = this.citiesAll.length;
        for (let i = 0; i < LEN; i++) {
          const CITY = this.citiesAll[i];
          if (lodashGet(CITY, "properties.code")) {
            AOI_IDS.forEach((id) => {
              if (CITY.properties.code.toUpperCase() === id.toUpperCase()) {
                cities.push(CITY);
              }
            });
          }
        }
      }
      if (cities.length) {
        cities = lodashOrderBy(cities, ["properties.displayName"], ["asc"]);
        return cities;
      }
      return null;
    },
  },
  methods: {
    async mapLoaded() {
      this.map.resize();
      if (!this.isWorldWideAOI) {
        if (this.hasGeometry) {
          this.drawAoi();
        } else {
          this.citiesAll = await getAllCities();
          if (this.cities) {
            this.addCitiesLayer();
          } else {
            this.countriesAll = await getAllCountries();
            this.addCountriesLayer();
          }
        }
      }
      if (!this.isWorldWideAOI) {
        this.fitToFeature();
      }
    },
    mapStyleData() {
      if (this.hasCountriesOrCities) {
        if (this.countries) {
          this.highlightCountries();
        } else {
          this.highlightCities();
        }
      }
    },
    drawAoi() {
      if (this.value && !this.value.properties) {
        return;
      }
      const SOURCE = {
        type: "FeatureCollection",
        features: [JSON.parse(JSON.stringify(this.value))],
      };

      const PARAMS = {
        id: "user-aoi",
      };

      const options = getDefaultPolygonOptions(PARAMS);

      mapPolygonData({
        id: PARAMS.id,
        data: SOURCE,
        polygonOptions: options,
        map: this.map,
      });
    },
    addCountriesLayer() {
      this.removeCountriesLayer();
      let params = {
        id: "country",
        type: "country",
        sourceId: "source-country",
        layerId: this.countriesLayerId,
        map: this.map,
        aoiCode: this.value.properties.aoiIds,
        aoiCodeField: this.countriesLayerFieldId,
        aoiCodeFillColor: "rgba(0, 174, 66, 0.35)",
      };
      addCountriesToMap(params);
    },
    addCitiesLayer() {
      this.removeCitiesLayer();
      let params = {
        id: "city",
        type: "city",
        sourceId: "source-city",
        layerId: this.citiesLayerId,
        map: this.map,
        aoiCode: this.value.properties.aoiIds,
        aoiCodeField: this.citiesLayerFieldId,
        aoiCodeFillColor: "rgba(0, 174, 66, 0.35)",
      };
      addCitiesToMap(params);
    },
    removeCountriesLayer() {
      removeMapData(this.map, this.countriesLayerId);
    },
    removeCitiesLayer() {
      removeMapData(this.map, this.citiesLayerId);
    },
    fitToFeature() {
      lodashDebounce(() => {
        let features = [this.value];
        if (this.hasCountriesOrCities) {
          let layerId = this.countriesLayerId;
          let fieldId = this.countriesLayerFieldId;
          if (this.cities) {
            layerId = this.citiesLayerId;
            fieldId = this.citiesLayerFieldId;
          }
          features = this.map.queryRenderedFeatures(null, {
            layers: [layerId],
            filter: [
              "match",
              ["get", fieldId],
              this.value.properties.aoiIds,
              true,
              false,
            ],
          });
        }
        if (
          features &&
          Array.isArray(features) &&
          features.length &&
          lodashGet(features[0], "geometry")
        ) {
          const BBOX = getBboxFromFeatures(features);
          try {
            this.map.fitBounds(BBOX, { padding: 40 });
          } catch (error) {
            console.error(error);
          }
        }
      }, 1000)();
    },
    highlightCountries() {
      if (this.map.getLayer(this.countriesLayerId)) {
        let items =
          this.value.properties.aoiIds &&
          Array.isArray(this.value.properties.aoiIds) &&
          this.value.properties.aoiIds.length
            ? this.value.properties.aoiIds
            : "none";

        this.map.setPaintProperty(this.countriesLayerId, "fill-opacity", [
          "match",
          ["get", this.countriesLayerFieldId],
          items,
          0.99,
          0.01,
        ]);

        this.map.setPaintProperty(this.countriesLayerId, "fill-color", [
          "match",
          ["get", this.countriesLayerFieldId],
          items,
          "rgba(0, 174, 66, 0.35)",
          "rgba(255, 255, 255, 0.01)",
        ]);

        this.map.setPaintProperty(this.countriesLayerId, "fill-outline-color", [
          "match",
          ["get", this.countriesLayerFieldId],
          items,
          "rgba(0, 174, 66, 0.99)",
          "rgba(255, 255, 255, 0.2)",
        ]);
      }
    },
    highlightCities() {
      if (this.map.getLayer(this.citiesLayerId)) {
        let items =
          this.value.properties.aoiIds &&
          Array.isArray(this.value.properties.aoiIds) &&
          this.value.properties.aoiIds.length
            ? this.value.properties.aoiIds
            : "none";

        this.map.setPaintProperty(this.citiesLayerId, "fill-opacity", 0.99);

        this.map.setPaintProperty(this.citiesLayerId, "fill-color", [
          "match",
          ["get", this.citiesLayerFieldId],
          items,
          "rgba(0, 174, 66, 0.35)",
          "rgba(255, 255, 255, 0.01)",
        ]);

        this.map.setPaintProperty(
          this.citiesLayerId,
          "fill-outline-color",
          "rgba(255, 255, 255, 0.75)"
        );
      }
    },
  },
  beforeDestroy() {
    if (this.hasWebGL) {
      this.removeCountriesLayer();
      this.removeCitiesLayer();
    }
    this.countriesAll = null;
    this.citiesAll = null;
  },
};
</script>

<style scoped>
.aoi-editor-container {
  padding: 0;
}
.aoi-chip >>> .v-chip__content {
  height: 24px;
  border-radius: 16px;
  padding: 0 6px;
}
</style>
